import { user_data } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { HttpClient } from 'src/services/utils/http-client';
import { handle_error_on_upload_image } from 'src/services/native_container_requests_android';
import { postReq } from 'src/services/http';
import { toast } from '@Shelly/shelly-design-system/index';
import { profiles } from 'src/stores/features/profiles';
import { _format } from 'src/stores/features/device-notifications';

/**
 *
 * @param event when file selector  return some files
 * @param params this for determine img type(for now device is tested)
 * @returns res.data and we can use it like other posReq and to do something when res.isok
 */
export const upload_image = async (event, type) => {
	let fileUploadOptions = {};
	switch (type) {
		case 'room':
		case 'group':
		case 'scene':
		case 'alarm':
		case 'dashboard':
			fileUploadOptions = {
				thumbWidth: 440,
				thumbHeight: 160,
				transparent: true,
			};
			break;
		case 'home_background':
			fileUploadOptions = {
				thumbWidth: 1440,
				thumbHeight: 1025,
				transparent: false,
			};
			break;
		case 'device':
		case 'avatar':
		default:
			fileUploadOptions = {
				thumbWidth: 128,
				thumbHeight: 128,
				transparent: true,
			};
			break;
	}
	return new FileUpload(fileUploadOptions)
		.init(event.target.files[0])
		.then(async (files: File[]) => {
			return await sendUploadImage(files, type);
		})
		.catch((err: string) => {
			return { isok: false, err };
		});
};

const sendUploadImage = async (files: File[], type: string) => {
	const stored_user = get(user_data),
		formData: any = new FormData();
	formData.append('user_image', files[0]);
	if (files[1]) formData.append('thumb_user_image', files[1]);
	formData.append('type', type);
	try {
		const res = await new HttpClient(stored_user.user_api_url).post(
			`/user_files/image_upload`,
			formData,
			{
				headers: {
					//this is config for headers
					Accept: 'application/json',
					Authorization: `Bearer ${stored_user.token}`,
					'Accept-Language': 'en-US,en;q=0.8',
				},
			},
		);
		return { ...res.data, isok: true };
	} catch (error) {
		handle_error_on_upload_image();
		if (error?.response?.data?.errors) {
			return {
				isok: false,
				err: get(format)(`images_upload_err_${Object.keys(error?.response?.data?.errors)[0]}`, {
					default: Object.values(error?.response?.data?.errors)[0] as string,
				}),
			};
		}
		return {
			isok: false,
			err: get(format)('images_upload_err_toast', {
				default: 'There was an error during upload picture.',
			}),
		};
	}
};

class FileUpload {
	private options = {
		fileTypes: '.jpg,.jpeg,.heif,.png',
		maxFileSize: 8,
		minWidth: 64,
		minHeight: 64,
		maxWidth: 6720,
		maxHeight: 6720,
		outputWidth: 1920,
		outputHeight: 1080,
		thumbWidth: 0,
		thumbHeight: 0,
		backgroundColor: null,
		transparent: false,
	};
	private resolve = (_files = []) => {};
	private reject = (_err: string = '') => {};

	constructor(options = null) {
		this.options = { ...this.options, ...options };
	}
	public init(file: File) {
		console.log('Init file upload: ', file.name, file.type);
		return new Promise((resolve, reject) => {
			this.resolve = resolve;
			this.reject = reject;
			const fileTypes = this.options.fileTypes.toLowerCase(),
				fileType = file.type.toLowerCase().split('/')[1];
			if (fileTypes.indexOf(fileType) == -1 && !file.name.toLowerCase().endsWith('.heif')) {
				console.log('Unsuported file format: ', fileType);
				this.errorHandler(415);
			} else if (file.size / Math.pow(1024, 2) > this.options.maxFileSize) {
				this.errorHandler('maxFileSize', this.options.maxFileSize + 'Mb');
			} else if (fileType == 'heif' || file.name.toLowerCase().endsWith('.heif')) {
				console.log('Should convert HEIF image: ', file.name);
				this.convertHeicImage(file);
			} else {
				this.createImage(file);
			}
		});
	}

	private async convertHeicImage(file: File) {
		//This will remain for MAC devices and eventually old IOS devices.
		import('heic2any').then(({ default: heic2any }) => {
			console.log('HEIF Converter loaded.');
			heic2any({
				blob: file,
				toType: 'image/jpeg',
				quality: 0,
			})
				.then((blob: Blob) => {
					this.createImage(<File>blob);
				})
				.catch((_) => {
					console.log('HEIF Converter error.');
					this.errorHandler(415);
				});
		});
	}

	private createImage(file: File) {
		const img = new Image(),
			windowURL = window.URL || window.webkitURL;
		img.onerror = () => {
			this.errorHandler(415);
			windowURL.revokeObjectURL(img.src);
		};
		img.onload = () => {
			this.readImage(file, img);
			windowURL.revokeObjectURL(img.src);
		};
		img.src = windowURL.createObjectURL(file);
	}
	private readImage(file: File, img: HTMLImageElement) {
		if (this.options.minWidth > 0 && img.width < this.options.minWidth) {
			return this.errorHandler('minWidth', this.options.minWidth + 'px');
		} else if (this.options.minHeight > 0 && img.height < this.options.minHeight) {
			return this.errorHandler('minHeight', this.options.minHeight + 'px');
		} else if (this.options.maxWidth > 0 && img.width > this.options.maxWidth) {
			return this.errorHandler('maxWidth', this.options.maxWidth + 'px');
		} else if (this.options.maxHeight > 0 && img.height > this.options.maxHeight) {
			return this.errorHandler('maxHeight', this.options.maxHeight + 'px');
		}
		const canvas = document.createElement('canvas'),
			size = this.resizeThumb(
				img.width,
				img.height,
				this.options.outputWidth,
				this.options.outputHeight,
			);
		const needsResize = size.width !== img.width || size.height !== img.height;
		const needsBackgroundFill = this.options.backgroundColor && !this.options.transparent;
		const type = `image/${this.options.transparent && file.type == 'image/png' ? 'png' : 'jpeg'}`;
		const fileName = file.name?.split('.')?.[0] || 'file_no_name';
		const lastModified = new Date().getTime();
		const files = [];

		// When no resize or background fill is needed and the output format matches the input,
		// skip canvas re-encoding for the main image. Canvas PNG encoding uses basic DEFLATE
		// without advanced compression, which can inflate file size beyond server limits.
		const canUseOriginalFile = !needsResize && !needsBackgroundFill && file.type === type;

		const createThumbnail = () => {
			if (this.options.thumbWidth > 0 && this.options.thumbHeight > 0) {
				const thumbSize = this.resizeThumb(
					size.width,
					size.height,
					this.options.thumbWidth,
					this.options.thumbHeight,
				);
				canvas.width = thumbSize.width;
				canvas.height = thumbSize.height;
				const ctx = canvas.getContext('2d');
				if (needsBackgroundFill) {
					ctx.fillStyle = this.options.backgroundColor;
					ctx.fillRect(0, 0, canvas.width, canvas.height);
				}
				ctx.drawImage(img, 0, 0, thumbSize.width, thumbSize.height);
				canvas.toBlob((thumbBlob) => {
					if (!thumbBlob) {
						this.errorHandler(500);
						return;
					}
					const thumbFile = new File([thumbBlob], fileName + '_thumb.' + type.split('/')[1], {
						type,
						lastModified,
					});
					files.push(thumbFile);
					this.resolve(files);
				}, type);
			} else {
				this.resolve(files);
			}
		};

		if (canUseOriginalFile) {
			files.push(file);
			createThumbnail();
		} else {
			img.width = canvas.width = size.width;
			img.height = canvas.height = size.height;
			const ctx = canvas.getContext('2d');
			if (needsBackgroundFill) {
				ctx.fillStyle = this.options.backgroundColor;
				ctx.fillRect(0, 0, canvas.width, canvas.height);
			}
			ctx.drawImage(img, 0, 0, img.width, img.height);
			canvas.toBlob((blob) => {
				if (!blob) {
					this.errorHandler(500);
					return;
				}
				const blobFile = new File([blob], fileName + '.' + type.split('/')[1], {
					type,
					lastModified,
				});
				files.push(blobFile);
				createThumbnail();
			}, type);
		}
	}
	private resizeThumb(
		width: number,
		height: number,
		maxWidth: number,
		maxHeight: number,
	): { width: number; height: number } {
		const ratio = height / width,
			widthRatio = maxHeight / ratio,
			heightRatio = maxWidth * ratio;
		if (maxWidth > 0 && width >= maxWidth && heightRatio >= maxHeight) {
			width = maxWidth;
			height = heightRatio;
		}
		if (maxHeight > 0 && height >= maxHeight && widthRatio >= maxWidth) {
			height = maxHeight;
			width = widthRatio;
		}
		return { width: Math.ceil(width), height: Math.ceil(height) };
	}
	private errorHandler(code: string | number, limit: string = null) {
		const $format = get(format);
		let error = '';
		switch (code) {
			case 204:
				error = $format('file_reader_error', {
					default: 'FileReader file error content',
				});
				break;
			case 400:
				error = $format('bad_request_error', { default: 'Bad request error' });
				break;
			case 401:
				error = $format('security_error', { default: 'Security Error' });
				break;
			case 404:
				error = $format('file_not_found', { default: 'File not found' });
				break;
			case 413:
				error = $format('file_too_large', { default: 'File is too large' });
				break;
			case 415:
				error = $format('unsupported_file_type', {
					default: 'Unsupported file type',
				});
				break;
			case 500:
				error = $format('server_error', { default: 'Internal server error' });
				break;
			case 'maxFileSize':
				error = $format('file_too_large', {
					default: 'File is too large, maximum file size is ' + limit,
				});
				break;
			case 'minWidth':
			case 'minHeight':
				error = $format('min_image_dimension_error', {
					default: 'The minimum image dimension is ' + limit,
				});
				break;
			case 'maxWidth':
			case 'maxHeight':
				error = $format('max_image_dimension_error', {
					default: 'The maximum image dimension is ' + limit,
				});
				break;
			default:
				error = $format('images_upload_err_toast', {
					default: 'There was an error during upload picture.',
				});
		}
		this.reject(error);
		return false;
	}
}

export const handleRequest = async (type) => {
	const res = await postReq('/user_files/image_list', {
		type: type,
	});
	if (res.isok) {
		const list = res.data.files;
		return list;
	} else {
		toast.open({ type: 'danger', title: 'error_global' });
	}
};

export const deleteImageFromStore = async (removeImageData) => {
	if (removeImageData.galleryType?.avatar === 'Avatars') {
		const activeProfile = get(profiles).find((item) => item.isSelected);

		if (activeProfile?.acc_avatar?.image?.includes(removeImageData?.image?.thumb)) {
			profiles.edit({
				...activeProfile,
				acc_avatar: {
					...activeProfile.acc_avatar,
					image: '',
					backgroundColor: 'color-green',
				},
			});
		}
	} else if (removeImageData.galleryType.device === 'devices') {
		const devicesArr = Object.values(get(devices));
		const deleteImgFilename = removeImageData.image.filename;

		const devicesWithDelImage = devicesArr.filter((el) => el?.image?.includes(deleteImgFilename));

		devicesWithDelImage.forEach(async (device) => {
			let editedDevice = {
				...device,
				image: '',
				icon: 'fak fa-solid-shelly',
			};
			devices.update(editedDevice);

			let int_res = await postReq('interface/device/save', {
				id: editedDevice.id,
				data: JSON.stringify(editedDevice),
			});
			if (int_res.isok) {
			} else {
				toast.open({
					type: 'danger',
					title: _format('error_global'),
				});
			}
		});
	}
};
